/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, {
  useEffect, useRef, useState
} from 'react';
import { useMap } from 'react-leaflet';
import L, {
  } from 'leaflet';
import {
  NetworkTopologyClusterElementsEnum,
  NetworkTopologyElementsEnum,
  NetworkTopologyMapData
 } from 'data/network-topology/network-topology.types';
import _ from 'lodash';
import * as actions from 'data/accessFunction/accessFunction.actions';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as templateSelectors from 'data/template/template.selectors';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import MapHelper, { NeEdge } from 'components/topology/utils/MapHelper';
import { getNetworkTopologyMapConfig } from 'data/network-topology/network-topology.adapter';
import './map.scss';
import NeClusterGroupGenerator from './NeClusterGroupGenerator';
import 'leaflet-spin/leaflet.spin';
import 'spin.js/spin';

const mapState = (state: RootState) => ({
  enabledCollectionFunctionTemplates: templateSelectors.getEnabledCollectionFunctionFormTemplatesByKey(state)
});

const mapDispatch = {
  goToAccessFunctionView: actions.goToAccessFunctionView,
  selectAccessFunction: actions.selectAccessFunction,
  selectCollectionFunction: collectionFunctionActions.selectCollectionFunction,
  goToCollectionFunctionView: collectionFunctionActions.goToCollectionFunctionView,
  getTemplates: warrantActions.getAllTemplates
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

type RouterParams = {
  history: string;
};

type Props = {
  mapData: NetworkTopologyMapData;
  setClusterExpansionList: any;
} & PropsFromRedux &
  PropsFromRouter;

let hoveredNe: any;

const ClusterGroupWrapper = React.memo(({
  mapData, setClusterExpansionList, enabledCollectionFunctionTemplates, getTemplates
}: Props) => {
  const afClusterGroupRef = useRef<L.MarkerClusterGroup>(null);
  const cfClusterGroupRef = useRef<L.MarkerClusterGroup>(null);
  const ccpagClusterGroupRef = useRef<L.MarkerClusterGroup>(null);
  const xcipioClusterGroupRef = useRef<L.MarkerClusterGroup>(null);
  const mdf2ClusterGroupRef = useRef<L.MarkerClusterGroup>(null);
  const mdf3ClusterGroupRef = useRef<L.MarkerClusterGroup>(null);
  const admfClusterGroupRef = useRef<L.MarkerClusterGroup>(null);
  const map = useMap();
  const mapConfig = getNetworkTopologyMapConfig();
  const [lines, setLines] = useState<NeEdge[]>([]);
  const homeZoom = mapConfig.mapPosition.zoom;

  const mapHelper = useRef<MapHelper | null>(null);
  const handleAnimationEnd = () => {
    const currZoom = map.getZoom();
    if (currZoom !== homeZoom) {
      setLines([]);
      buildGraph();
    } else {
      buildGraph();
      const edges = mapHelper.current?.getAllConnections();
      setLines(edges || []);
    }
    if (hoveredNe && (hoveredNe as any)._icon) {
      const image = getImageElement((hoveredNe as any)._icon);
      image.className ='marker-shadow';
      hoveredNe = undefined;
    }
  };

  const onDragEnd = (event: any) => {
    const marker = event.target;
    const position = marker.getLatLng();

    const afClusterGroup = afClusterGroupRef.current;
    const afClusters = (afClusterGroup as any)._featureGroup._layers;
    Object.entries(afClusters).map((af) => {
      if (
        !(af[1] instanceof L.MarkerCluster) &&
        af[1] instanceof L.Marker
      ) {
        const marker = af[1];
        const edges = mapHelper.current?.getAllConnections();
        if (edges) {
          const connections = [...edges];
          connections.forEach((line) => {
            if (line.sourceLeafletId === (marker as any)._leaflet_id) {
              line.sourceLocation = position;
            }
          });
          buildGraph();
          setLines(connections);
        }
      }
    });
  }

  const buildGraph = () => {
    if (
      afClusterGroupRef.current &&
      cfClusterGroupRef.current &&
      xcipioClusterGroupRef.current &&
      ccpagClusterGroupRef.current &&
      mdf2ClusterGroupRef.current &&
      mdf3ClusterGroupRef.current &&
      admfClusterGroupRef.current &&
      mapData
    ) {
      mapHelper.current = new MapHelper(
        afClusterGroupRef.current,
        cfClusterGroupRef.current,
        xcipioClusterGroupRef.current,
        ccpagClusterGroupRef.current,
        mdf2ClusterGroupRef.current,
        mdf3ClusterGroupRef.current,
        admfClusterGroupRef.current,
        mapData,
        map
      );
      mapHelper.current.buildGraph();
    }
  };

  useEffect(() => {
    if (_.isEmpty(enabledCollectionFunctionTemplates)) {
      getTemplates();
    }
  }, [getTemplates])

  useEffect(() => {
    if (
      afClusterGroupRef.current &&
      cfClusterGroupRef.current &&
      xcipioClusterGroupRef.current &&
      mapData
    ) {
      buildGraph();
      if (map.getZoom() === homeZoom) {
        const edges = mapHelper.current?.getAllConnections();
        setLines(edges || []);
      }
    }
  }, [afClusterGroupRef, cfClusterGroupRef, xcipioClusterGroupRef, ccpagClusterGroupRef, mdf2ClusterGroupRef, mdf3ClusterGroupRef, admfClusterGroupRef, mapData]);

  const getImageElement = (domElement: any) => {
    return domElement.querySelector('img');
  }
  const onHover = (event: any) => {
    if (map.getZoom() === homeZoom) {
      return null;
    }
    let target = event.propagatedFrom;
    if (!target) {
      target = event.target;
    }
    // buildGraph();
    setLines([]);
    if (mapHelper.current) {
      const marker = mapHelper.current.getMarkerByLeafletId(target._leaflet_id);
      if (hoveredNe && (hoveredNe as any)._icon) {
        const image = getImageElement((hoveredNe as any)._icon);
        image.className = 'marker-shadow';
      }
      hoveredNe = target;
      const image = getImageElement((target as any)._icon);
      if (marker.markerType === NetworkTopologyClusterElementsEnum.CollectionFunctionCluster) {
        image.className ='marker-shadow-hover-cf';
      } else if (marker.markerType === NetworkTopologyClusterElementsEnum.AccessFunctionCluster) {
        image.className ='marker-shadow-hover-af';
      } else if (marker.markerType === NetworkTopologyElementsEnum.CollectionFunction) {
        image.className ='marker-shadow-hover-cf';
      } else if (marker.markerType === NetworkTopologyElementsEnum.AccessFunction) {
        image.className ='marker-shadow-hover-af';
      } else {
        image.className = 'marker-shadow-hover-xcipio';
      }
      if (
        marker.markerType === NetworkTopologyClusterElementsEnum.CollectionFunctionCluster ||
        marker.markerType === NetworkTopologyElementsEnum.CollectionFunction
      ) {
        const edges: NeEdge[] = mapHelper.current.getAllPathsFromNodeToLeaf(
          target._leaflet_id,
          'in'
        );
        setLines(edges);
      } else {
        const edges: NeEdge[] = mapHelper.current.getAllPathsFromNodeToLeaf(
          target._leaflet_id
        );
        setLines(edges);
      }
    }
  };

  return (
    <React.Fragment>
      {/* {(map as any).spin(true)} */}
      <NeClusterGroupGenerator
        afClusterGroupRef={afClusterGroupRef}
        cfClusterGroupRef={cfClusterGroupRef}
        ccpagClusterGroupRef={ccpagClusterGroupRef}
        mdf2ClusterGroupRef={mdf2ClusterGroupRef}
        mdf3ClusterGroupRef={mdf3ClusterGroupRef}
        admfClusterGroupRef={admfClusterGroupRef}
        afMarkers={mapData.accessFunctions}
        cfMarkers={mapData.collectionFunctions}
        xcipioNodes={mapData.xcipioNodes}
        ccpags={mapData.ccpags}
        mdf2s={mapData.mdf2s}
        mdf3s={mapData.mdf3s}
        admfs={mapData.admfs}
        xcipioRef={xcipioClusterGroupRef}
        onHover={onHover}
        mapHelper={mapHelper}
        lines={lines}
        handleZoom={handleAnimationEnd}
        onDragEnd={onDragEnd}
        setClusterExpansionList={setClusterExpansionList}
      />
      {/* {setTimeout(() => {
        (map as any).spin(false);
      }, 3000)} */}
    </React.Fragment>
  );
});

export default withRouter(connector(ClusterGroupWrapper));
